import { Injectable, ChangeDetectorRef } from '@angular/core';

declare const wiz: any;

export class ChatMessage {
    public role: 'user' | 'assistant' = 'user';
    public content: string = '';
    public streaming: boolean = false;
    public citations: any[] = [];
    public messageId: number | null = null;
    public intent: string | null = null;
    public feedback: number = 0;
    public error: boolean = false;
    public tool_calls: any[] = [];
    public faq: any = null;
}

@Injectable({ providedIn: 'root' })
export class Service {
    public ready: boolean = false;
    public cdr: ChangeDetectorRef | null = null;
    public user: any = null;
    private navigator: ((path: string) => any) | null = null;
    private apiReadCache: Map<string, { expiresAt: number; data: any }> = new Map();
    private apiReadInflight: Map<string, Promise<any>> = new Map();

    constructor() { }

    public async init() {
        if (this.ready) return;
        this.ready = true;
    }

    public bindCdr(cdr: ChangeDetectorRef) {
        this.cdr = cdr;
    }

    public bindNavigator(navigator: ((path: string) => any) | null) {
        this.navigator = navigator;
    }

    public async render() {
        try { if (this.cdr) this.cdr.detectChanges(); } catch (e) { }
    }

    public async href(path: string) {
        const current = `${location.pathname}${location.search}${location.hash}`;
        if (current === path) return;
        if (this.navigator) {
            await this.navigator(path);
            return;
        }
        location.href = path;
    }

    public async hrefFull(path: string) {
        location.href = path;
    }

    public alert(message: string) {
        window.alert(message);
    }

    public confirm(message: string): boolean {
        return window.confirm(message);
    }

    private responseCode(res: Response, body: any): number {
        return typeof body?.code === 'number' ? body.code : res.status;
    }

    private responseData(body: any): any {
        return body?.data?.data ?? body?.data ?? null;
    }

    private cloneData(data: any): any {
        if (data === null || data === undefined) return data;
        try {
            return JSON.parse(JSON.stringify(data));
        } catch (e) {
            return data;
        }
    }

    private stableParamKey(value: any): string {
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) return `[${value.map((v) => this.stableParamKey(v)).join(',')}]`;
        if (typeof value === 'object' && !(value instanceof File) && !(value instanceof Blob)) {
            return `{${Object.keys(value).sort().map((key) => `${key}:${this.stableParamKey(value[key])}`).join(',')}}`;
        }
        return String(value);
    }

    private requestKey(resource: string, action: string, params: Record<string, any>): string {
        return `${resource}/${action}:${this.stableParamKey(params || {})}`;
    }

    // ---------------- Auth ----------------
    public async login(email: string, password: string): Promise<{ ok: boolean; message?: string; user?: any }> {
        try {
            const form = new FormData();
            form.append('email', email);
            form.append('password', password);
            const res = await fetch('/api/ggeea/auth/login', { method: 'POST', body: form, credentials: 'include' });
            const body = await res.json();
            if (this.responseCode(res, body) === 200) {
                this.user = this.responseData(body);
                return { ok: true, user: this.user };
            }
            return { ok: false, message: body.message || '로그인에 실패했습니다.' };
        } catch (e: any) {
            return { ok: false, message: e.message || String(e) };
        }
    }

    public async logout() {
        try {
            await fetch('/api/ggeea/auth/logout', { method: 'POST', credentials: 'include' });
        } catch (e) { }
        this.user = null;
        this.href('/admin/login');
    }

    public async me(): Promise<any | null> {
        try {
            const res = await fetch('/api/ggeea/auth/me', { credentials: 'include' });
            const body = await res.json();
            if (this.responseCode(res, body) !== 200) {
                this.user = null;
                return null;
            }
            this.user = this.responseData(body);
            return this.user;
        } catch (e) {
            this.user = null;
            return null;
        }
    }

    // ---------------- Admin API helper ----------------
    private async requestAdminApi(resource: string, action: string, params: Record<string, any> = {}, timeoutMs: number = 0): Promise<any> {
        const form = new FormData();
        for (const [k, v] of Object.entries(params)) {
            if (v === null || v === undefined) continue;
            if (Array.isArray(v) || (typeof v === 'object' && !(v instanceof File) && !(v instanceof Blob))) {
                form.append(k, JSON.stringify(v));
            } else if (v instanceof File || v instanceof Blob) {
                form.append(k, v);
            } else {
                form.append(k, String(v));
            }
        }
        const controller = timeoutMs > 0 && typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = controller ? window.setTimeout(() => controller.abort(), timeoutMs) : null;
        const res = await fetch(`/api/ggeea/admin/${resource}/${action}`, {
            method: 'POST',
            body: form,
            credentials: 'include',
            signal: controller ? controller.signal : undefined,
        }).finally(() => {
            if (timer !== null) window.clearTimeout(timer);
        });
        const body = await res.json().catch(() => ({}));
        const code = this.responseCode(res, body);
        if (code !== 200) {
            throw new Error(body.message || `API ${resource}/${action} failed (${code})`);
        }
        return this.responseData(body) ?? body;
    }

    public async api(resource: string, action: string, params: Record<string, any> = {}): Promise<any> {
        return this.requestAdminApi(resource, action, params);
    }

    public async apiHarness(
        resource: string,
        action: string,
        params: Record<string, any> = {},
        opts: { cacheTtlMs?: number; timeoutMs?: number; force?: boolean } = {},
    ): Promise<any> {
        const key = this.requestKey(resource, action, params);
        const now = Date.now();
        const ttl = Number(opts.cacheTtlMs || 0);
        const cached = this.apiReadCache.get(key);
        if (!opts.force && cached && cached.expiresAt > now) {
            return this.cloneData(cached.data);
        }

        const pending = this.apiReadInflight.get(key);
        if (!opts.force && pending) {
            return pending.then((data) => this.cloneData(data));
        }

        const request = this.requestAdminApi(resource, action, params, Number(opts.timeoutMs || 0))
            .then((data) => {
                if (ttl > 0) {
                    this.apiReadCache.set(key, { expiresAt: Date.now() + ttl, data: this.cloneData(data) });
                }
                return data;
            })
            .finally(() => this.apiReadInflight.delete(key));
        this.apiReadInflight.set(key, request);
        return request.then((data) => this.cloneData(data));
    }

    // ---------------- Chat SSE ----------------
    public async streamChat(opts: {
        query: string;
        sessionId: string | null;
        history: { role: string; content: string }[];
        provider?: string;
        onEvent: (ev: { type: string; payload: any }) => void;
    }) {
        const form = new FormData();
        form.append('query', opts.query);
        if (opts.sessionId) form.append('session_id', opts.sessionId);
        if (opts.provider) form.append('provider', opts.provider);
        form.append('history', JSON.stringify(opts.history || []));
        const res = await fetch('/api/ggeea/chat/stream', {
            method: 'POST', body: form, credentials: 'include',
            headers: { Accept: 'text/event-stream' },
        });
        if (!res.body) throw new Error('no body');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const events = buffer.split('\n\n');
            buffer = events.pop() || '';
            for (const ev of events) {
                const dataLines = ev.split('\n').filter(l => l.startsWith('data:'));
                for (const line of dataLines) {
                    const raw = line.slice(5).trim();
                    if (!raw) continue;
                    try {
                        const json = JSON.parse(raw);
                        if (json.type) {
                            opts.onEvent(json);
                        } else if (json.session_id) {
                            opts.onEvent({ type: 'session', payload: json });
                        }
                    } catch (e) { }
                }
                const eventMatch = ev.match(/^event:\s*(\w+)/m);
                const dataMatch = ev.match(/data:\s*(.*)$/m);
                if (eventMatch && dataMatch && eventMatch[1] === 'session') {
                    try {
                        opts.onEvent({ type: 'session', payload: JSON.parse(dataMatch[1]) });
                    } catch (e) { }
                }
            }
        }
    }

    public async feedback(messageId: number, value: number, reason?: string) {
        const form = new FormData();
        form.append('message_id', String(messageId));
        form.append('feedback', String(value));
        if (reason) form.append('reason', reason);
        await fetch('/api/ggeea/chat/feedback', { method: 'POST', body: form, credentials: 'include' });
    }

    public async logFaqTurn(opts: {
        question: string;
        answer: string;
        sessionId?: string | null;
        faqId?: string | null;
    }): Promise<any> {
        const form = new FormData();
        form.append('question', opts.question);
        form.append('answer', opts.answer);
        if (opts.sessionId) form.append('session_id', opts.sessionId);
        if (opts.faqId) form.append('faq_id', opts.faqId);

        const res = await fetch('/api/ggeea/chat/faq_message', {
            method: 'POST',
            body: form,
            credentials: 'include',
        });
        const body = await res.json().catch(() => ({}));
        const code = this.responseCode(res, body);
        if (code !== 200) {
            throw new Error(body.message || `FAQ log failed (${code})`);
        }
        return this.responseData(body) ?? body;
    }
}
